<template>
    <div
        v-if="tooltip !== ''"
        class="RPTooltip"
    >
        <Tip
            v-if="showTooltip || (useParentToggle && parentToggle)"
            :tooltip="tooltip"
        />
        <span
            class="RPTooltip__icon"
            @mouseover="openTooltip"
            @mouseout="closeTooltip"
        >
            <slot>
                <StreamlineIcon
                    class="RPTooltip__svg"
                    :icon="defaultIcon"
                    :fill="defaultIconColor"
                    :stroke="defaultIconStroke"
                    :size="size"
                />
            </slot>
        </span>
    </div>
</template>
<script>
import stat from 'status/index';
import Tip from './RPTooltip/tooltip.vue';
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {InformationCircle} from "@reviewpush/streamline-bold/lib/interface-essential/Alerts";
import {CheckCircle1} from "@reviewpush/streamline-bold/lib/interface-essential/FormValidation";
import {Delete1} from "@reviewpush/streamline-regular/lib/interface-essential/Delete";
import {AlertTriangle} from "@reviewpush/streamline-bold/lib/interface-essential/Alerts";
import {
    success,
    error,
    warning,
    textDark,
    grey,
    white
} from 'style/_colors.scss';

export default {
    name: 'RPTooltip',
    components: {
        StreamlineIcon,
        Tip
    },
    props: {
        /**
         * The tooltip text
         */
        tooltip: {
            type: String,
            required: true,
        },
        parentToggle: {
            type: Boolean,
            default: false,
        },
        useParentToggle: {
            type: Boolean,
            default: false,
        },
        /**
         * The pixel size of the tooltip icon
         */
        size: {
            type: Number,
            default: 12
        },
        /**
         * A streamline icon
         */
        icon: {
            type: [Array, Object],
            default: () => {
                return InformationCircle;
            }
        },
        /**
         * The fill color of the Streamline icon (any valid CSS color)
         */
        fill: {
            type: String,
            default: grey
        },
        /**
         * The stroke color of the Streamline icon (any valid CSS color)
         */
        stroke: {
            type: String,
            default: white
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    data(){
        return {
            showTooltip: false
        };
    },
    computed: {
        defaultIcon(){
            switch(this.status){
            case stat.WARNING:
                return AlertTriangle;
            case stat.SUCCESS:
                return CheckCircle1;
            case stat.ERROR:
                return Delete1;
            default:
                return this.icon;
            }
        },
        defaultIconColor(){
            switch(this.status){
            case stat.WARNING:
                return warning;
            case stat.SUCCESS:
                return success;
            case stat.ERROR:
                return error;
            default:
                return this.fill;
            }
        },
        defaultIconStroke(){
            switch(this.status){
            case stat.WARNING:
            case stat.SUCCESS:
            case stat.ERROR:
                return white;
            default:
                return this.stroke;
            }
        },
    },
    mounted() {
        if(!this.useParentToggle) {
            document.addEventListener('click', this.closeTooltip);
        }
    },
    destroyed() {
        if(!this.useParentToggle) {
            document.removeEventListener('click', this.closeTooltip);
        }
    },
    methods: {
        closeTooltip(){
            if(!this.useParentToggle) {
                this.showTooltip = false;
            }
        },
        openTooltip(){
            if(!this.useParentToggle) {
                this.showTooltip = true;
            }
        }
    },
};
</script>
<style lang="scss">
    .RPTooltip {
        width: fit-content;
        display: flex;
        position: relative;
        align-items: center;
        cursor: pointer;
        &__icon {
            display: contents;
        }
        &__svg {
            display: flex;
        }
    }
</style>

<docs>
```vue
import {Cookie} from "@reviewpush/streamline-regular/lib/food-drinks/CakesCandies";

<p>
    This is a paragraph that needs further explanation. <RPTooltip tooltip="More info" />
</p>
<p>
    This is a paragraph that needs further explanation. <RPTooltip tooltip="More info" :size="48" fill="red" />
</p>
<p>
    This is a paragraph that needs further explanation. <RPTooltip tooltip="More info" :icon="Cookie" :size="24" />
</p>
```
</docs>
